/**
 * Failed business events — `/ledger/failed-events`.
 *
 * What the posting worker could not turn into a journal, and the only action
 * available: put it back on the queue.
 *
 * Replay preserves the event's identity and payload — it never writes a
 * replacement — and posting stays idempotent because the journal's source
 * reference IS the event id. So replaying something that has since posted
 * cannot double-post it; it comes back rejected as already posted, which the
 * screen reports rather than hides.
 *
 * `attempts` is the count of committed attempts and replay never resets it.
 * Shown as-is: a number that keeps climbing is the honest record of a resolver
 * that keeps failing on the same thing.
 *
 * Four of the failure codes cannot be fixed by trying again — an unmapped
 * account, an invalid or missing rule set, a missing FX rate. Those rows say
 * so, because a retry that was always going to fail wastes the operator's
 * attention rather than the machine's.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RotateCcw } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
} from "@erp/ui";

import { formatDateTime } from "@/lib/format";
import { newIdempotencyKey } from "@/features/shared/ops-api";

import {
  listFailedBusinessEvents,
  needsConfigurationChange,
  replayBusinessEvents,
  type FailedBusinessEvent,
} from "../business-events-api";

const FAILED_KEY = "ledger/failed-business-events";

export interface FailedEventsPageProps {
  canReplay?: boolean;
}

export function FailedEventsPage({ canReplay = false }: FailedEventsPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [eventType, setEventType] = useState("");
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [rejected, setRejected] = useState<readonly { event_id: string; reason_code: string }[]>(
    [],
  );

  const failed = useQuery({
    queryKey: [FAILED_KEY, eventType],
    queryFn: async ({ signal }) => [
      ...(
        await listFailedBusinessEvents(
          { limit: 100, ...(eventType === "" ? {} : { event_type: eventType }) },
          signal,
        )
      ).items,
    ],
  });

  const stamp = (iso: string): string => {
    try {
      return formatDateTime(iso, i18n.language);
    } catch {
      return iso;
    }
  };

  const rows: FailedBusinessEvent[] = useMemo(() => failed.data ?? [], [failed.data]);
  const replayable = useMemo(() => rows.filter((row) => selected.has(row.id)), [rows, selected]);

  const toggle = (eventId: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });

  const replay = () => {
    if (replayable.length === 0) return;
    setBusy(true);
    setError(null);
    setOutcome(null);
    setRejected([]);
    void replayBusinessEvents(
      replayable.map((row) => row.id),
      newIdempotencyKey(),
    )
      .then(async (result) => {
        setOutcome(
          t(
            "ledger.failedEvents.replayed",
            "{{count}} event(s) are back on the queue. Their identity and payload are unchanged.",
            { count: result.accepted_event_ids.length },
          ),
        );
        setRejected(result.rejected);
        setSelected(new Set());
        await queryClient.invalidateQueries({ queryKey: [FAILED_KEY] });
      })
      .catch(() => setError(t("ledger.failedEvents.replayError", "The replay could not run.")))
      .finally(() => setBusy(false));
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">
          {t("ledger.failedEvents.title", "Failed events")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "ledger.failedEvents.subtitle",
            "What the posting worker could not turn into a journal. Replaying keeps the event's identity, so nothing can post twice.",
          )}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="failed-event-type">
            {t("ledger.failedEvents.eventType", "Event type")}
          </Label>
          <Input
            id="failed-event-type"
            dir="ltr"
            className="w-64 font-mono"
            placeholder={t("ledger.failedEvents.anyType", "Any type")}
            value={eventType}
            onChange={(event) => setEventType(event.target.value)}
          />
        </div>
        {canReplay ? (
          <Button type="button" disabled={busy || replayable.length === 0} onClick={replay}>
            <RotateCcw className="size-4" aria-hidden="true" />
            {t("ledger.failedEvents.replay", "Put {{count}} back on the queue", {
              count: replayable.length,
            })}
          </Button>
        ) : null}
      </div>

      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {outcome === null ? null : (
        <p role="status" className="text-sm text-muted-foreground">
          {outcome}
        </p>
      )}
      {rejected.length === 0 ? null : (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("ledger.failedEvents.rejectedTitle", "Not re-enqueued")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              {rejected.map((row) => (
                <li key={row.event_id} className="flex flex-wrap items-center gap-2">
                  <span dir="ltr" className="font-mono text-xs text-muted-foreground">
                    {row.event_id}
                  </span>
                  <Badge variant="outline">
                    {t(`ledger.replayRejection.${row.reason_code}`, row.reason_code)}
                  </Badge>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {failed.isLoading ? (
            <p className="p-4 text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
          ) : rows.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">
              {t("ledger.failedEvents.empty", "Nothing has failed. Every event became a journal.")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[52rem] text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase text-muted-foreground">
                    {canReplay ? <th scope="col" className="p-2" /> : null}
                    <th scope="col" className="p-2 text-start">
                      {t("ledger.failedEvents.event", "Event")}
                    </th>
                    <th scope="col" className="p-2 text-start">
                      {t("ledger.failedEvents.failure", "Why it failed")}
                    </th>
                    <th scope="col" className="p-2 text-end">
                      {t("ledger.failedEvents.attempts", "Attempts")}
                    </th>
                    <th scope="col" className="p-2 text-start">
                      {t("ledger.failedEvents.failedAt", "Failed")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-b last:border-0">
                      {canReplay ? (
                        <td className="p-2">
                          <Checkbox
                            checked={selected.has(row.id)}
                            onCheckedChange={() => toggle(row.id)}
                            aria-label={t("ledger.failedEvents.select", "Select this event")}
                          />
                        </td>
                      ) : null}
                      <td className="p-2">
                        <span className="block" dir="ltr">
                          {row.event_type}
                        </span>
                        <span className="block font-mono text-xs text-muted-foreground" dir="ltr">
                          {row.aggregate_type} · {row.id}
                        </span>
                      </td>
                      <td className="p-2">
                        <span className="block">
                          {t(`ledger.eventFailure.${row.error}`, row.error)}
                        </span>
                        {needsConfigurationChange(row.error) ? (
                          <span className="block text-xs text-muted-foreground">
                            {t(
                              "ledger.failedEvents.needsChange",
                              "Replaying alone will fail again — the configuration it needs is what is missing.",
                            )}
                          </span>
                        ) : null}
                      </td>
                      <td className="p-2 text-end tabular-nums" dir="ltr">
                        {row.attempts}
                      </td>
                      <td className="p-2 text-xs text-muted-foreground">{stamp(row.failed_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
